import { HEADLINE, attached, plainText } from "./notes";

// Choose boards: which boards the room sees, and in what order. A card is
// carried to its place in drag.js.

// A copy of a board for the Choose boards list: the same drawing the room
// sees, small, and inert — not a slide the deck pages through, no buttons,
// and its SVG ids its own, or the copy's clip paths would capture the
// original's (the devices board draws its phone and desk through two).
export function boardCopy(board, suffix) {
  const copy = board.cloneNode(true);
  copy.removeAttribute("aria-hidden");
  copy.removeAttribute("style");
  copy.classList.remove("is-current");
  delete copy.dataset.hidden;
  [copy, ...copy.querySelectorAll("[data-presentation-target], [data-action]")].forEach((node) => {
    node.removeAttribute("data-presentation-target");
    node.removeAttribute("data-action");
  });
  copy.querySelectorAll("img[data-src]").forEach((img) => {
    img.src = img.dataset.src;
    delete img.dataset.src;
  });

  const ids = new Map();
  copy.querySelectorAll("[id]").forEach((node) => {
    ids.set(node.id, `${node.id}--${suffix}`);
    node.id = ids.get(node.id);
  });
  if (ids.size) {
    copy.querySelectorAll("*").forEach((node) => {
      Array.from(node.attributes).forEach((attr) => {
        if (!attr.value.includes("#")) return;
        let value = attr.value;
        ids.forEach((to, from) => {
          value = value.replaceAll(`url(#${from})`, `url(#${to})`);
          if (value === `#${from}`) value = `#${to}`;
        });
        if (value !== attr.value) node.setAttribute(attr.name, value);
      });
    });
  }
  return copy;
}

export default {
  // Every board but the cover, in the order the room will see it, ticked
  // unless hidden. The cover opens every deck and is not the presenter's
  // to drop, nor is a chapter's opening page. A slide that draws several
  // boards moves as one.
  openChooser() {
    if (!this.hasChooserTarget) return;
    this.closeMenus();
    this.choice = this.slideTargets
      .map((board, i) => ({ board, slug: board.dataset.slideSlug, chapter: board.dataset.chapter || "", on: !this.hiddenAt(i) }))
      .filter((row) => row.slug && row.slug !== "cover" && !attached(row.board))
      .map((row) => ({ ...row, key: row.slug.replace(/-\d+$/, ""), part: Number(row.slug.match(/-(\d+)$/)?.[1] || 1) }));
    this.choiceOrder = this.keysOf(this.choice);
    this.choiceKeep = this.chooserKeepTargets.find((radio) => radio.checked)?.value;
    this.chooserTarget.hidden = false;
    this.renderKeep();
    this.renderChooser();
  },

  // The save choice says what it will do: the line under it, and Save's
  // own label.
  renderKeep() {
    const keep = this.chooserKeepTargets.find((radio) => radio.checked)?.value || "deck";
    if (this.hasChooserKeepNoteTarget) this.chooserKeepNoteTarget.textContent = this.chooserKeepNoteTarget.dataset[keep];
    if (this.hasChooserSaveTarget) this.chooserSaveTarget.textContent = this.chooserSaveTarget.dataset[keep];
  },

  closeChooser() {
    if (this.hasChooserTarget) this.chooserTarget.hidden = true;
  },

  chooserData(name) {
    try {
      return JSON.parse(this.chooserTarget.dataset[name] || "null");
    } catch {
      return null;
    }
  },

  keysOf(rows) {
    return [...new Set(rows.map((row) => row.key))];
  },

  renderChooser(focus) {
    const titles = this.titlesValue;
    const chapters = this.chaptersValue;
    const element = (tag, className, text) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    };

    this.chooserListTarget.replaceChildren(
      ...this.choice.map((row, i) => {
        const item = element("li", `deck-chooser-row${row.on ? "" : " is-off"}`);
        const label = element("label", "deck-chooser-label");
        const box = element("input");
        box.type = "checkbox";
        box.checked = row.on;
        box.dataset.row = i;
        box.dataset.action = "change->presentation#chooseRow keydown->presentation#chooseKey";
        box.setAttribute("aria-keyshortcuts", "Alt+ArrowLeft Alt+ArrowRight");
        const thumb = this.thumbFor(row);
        const name = element("span", "deck-chooser-name");
        name.append(element("span", "deck-chooser-number"), `${titles[row.key] || row.key}${row.part > 1 ? ` ${row.part}` : ""}`);
        const headline = row.board.querySelector(HEADLINE);
        const said = headline && plainText(headline);
        const detail = element("span", "deck-chooser-detail deck-label-sm", [chapters[row.chapter], said !== name.lastChild.textContent && said].filter(Boolean).join(" · "));
        label.append(box, thumb, name, detail);
        item.append(label);
        item.dataset.row = i;
        item.dataset.key = row.key;
        return item;
      }),
    );

    this.renderChapters();
    this.numberCards();
    if (focus) this.chooserTarget.querySelector(focus)?.focus();
  },

  // Each board's place in the room, as the cards stand: the cover is the
  // first, a board left out has none. Read off the cards, so it is right
  // while one is being carried too.
  numberCards() {
    let place = 1;
    this.chooserListTarget.querySelectorAll(".deck-chooser-row").forEach((card) => {
      const on = this.choice[Number(card.dataset.row)]?.on;
      if (on) place += 1;
      const number = card.querySelector(".deck-chooser-number");
      if (number) number.textContent = on ? String(place).padStart(2, "0") : "–";
    });
  },

  // The chapter buttons, lit when every board in the chapter is in.
  renderChapters() {
    const chapters = this.chaptersValue;
    const present = [...new Set(this.choice.map((row) => row.chapter).filter(Boolean))];
    const element = (tag, className, text) => {
      const node = document.createElement(tag);
      node.className = className;
      node.textContent = text;
      return node;
    };
    this.chooserChaptersTarget.replaceChildren(
      ...present.map((chapter) => {
        const rows = this.choice.filter((row) => row.chapter === chapter);
        const button = element("button", "deck-chooser-chip deck-label-sm", chapters[chapter] || chapter);
        button.type = "button";
        button.dataset.chapter = chapter;
        button.dataset.action = "click->presentation#chooseChapter";
        button.setAttribute("aria-pressed", String(rows.every((row) => row.on)));
        button.classList.toggle("is-partial", rows.some((row) => row.on) && !rows.every((row) => row.on));
        return button;
      }),
    );
  },

  // Each board's copy is made once and moved with its row from then on.
  // Kept by the board itself, not its slug: one copy per board, whatever
  // the boards are called.
  thumbFor(row) {
    this.thumbs ||= new Map();
    if (!this.thumbs.has(row.board)) {
      const frame = document.createElement("span");
      frame.className = "deck-chooser-thumb";
      frame.setAttribute("aria-hidden", "true");
      frame.append(boardCopy(row.board, `${row.slug}-${this.thumbs.size}`));
      this.thumbs.set(row.board, frame);
    }
    return this.thumbs.get(row.board);
  },

  // A tick changes the row it is on and the chapter buttons, nothing else:
  // the list stays where it was scrolled to.
  syncChooser() {
    this.chooserListTarget.querySelectorAll("input[data-row]").forEach((box) => {
      const row = this.choice[Number(box.dataset.row)];
      box.checked = row.on;
      box.closest(".deck-chooser-row").classList.toggle("is-off", !row.on);
    });
    this.renderChapters();
    this.numberCards();
  },

  chooseRow(event) {
    this.choice[Number(event.target.dataset.row)].on = event.target.checked;
    this.syncChooser();
  },

  // A whole chapter in or out: all of it, unless all of it is in already.
  chooseChapter(event) {
    const chapter = event.currentTarget.dataset.chapter;
    const rows = this.choice.filter((row) => row.chapter === chapter);
    const on = !rows.every((row) => row.on);
    rows.forEach((row) => (row.on = on));
    this.syncChooser();
    this.chooserChaptersTarget.querySelector(`[data-chapter="${chapter}"]`)?.focus();
  },

  // The boards in their slides: a slide that draws several boards (the
  // product pages) moves as one.
  choiceGroups() {
    const groups = [];
    this.choice.forEach((row) => {
      const last = groups[groups.length - 1];
      if (last && last[0].key === row.key) last.push(row);
      else groups.push([row]);
    });
    return groups;
  },

  // Alt and an arrow on a board's tick: the slide one place earlier or
  // later, for whoever sorts the deck from the keyboard.
  chooseKey(event) {
    if (!event.altKey) return;
    const step = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const row = this.choice[Number(event.target.dataset.row)];
    const groups = this.choiceGroups();
    const at = groups.findIndex((group) => group.includes(row));
    const to = at + step;
    if (to < 0 || to >= groups.length) return;
    [groups[at], groups[to]] = [groups[to], groups[at]];
    this.choice = groups.flat();
    this.renderChooser(`input[data-row="${this.choice.indexOf(row)}"]`);
    this.chooserListTarget.querySelectorAll(`.deck-chooser-row[data-key="${row.key}"]`).forEach((card) => card.classList.add("is-dropped"));
  },

  // Start again from the deck's own order: every board, or the cut a
  // manager takes upward.
  chooseFull() {
    this.chooseFrom(() => true);
  },

  chooseManagement() {
    const management = this.chooserData("management") || [];
    this.chooseFrom((row) => management.includes(row.key));
  },

  chooseFrom(keepRow) {
    const natural = this.chooserData("natural") || [];
    const rank = (row) => (natural.indexOf(row.key) + 1 || natural.length + 1) * 1000 + row.part;
    this.choice = [...this.choice].sort((a, b) => rank(a) - rank(b));
    this.choice.forEach((row) => (row.on = keepRow(row)));
    this.renderChooser();
  },

  saveChooser() {
    const keep = this.chooserKeepTargets.find((radio) => radio.checked)?.value || "deck";
    const order = this.keysOf(this.choice);
    const redraw = keep !== this.choiceKeep || order.join() !== this.choiceOrder.join();
    this.saveChoice({ hidden: this.choice.filter((row) => !row.on).map((row) => row.slug), order, keep }, redraw);
  },

  // Back to the saved cut: this deck's own choice forgotten.
  resetChooser() {
    this.saveChoice({ hidden: [], order: [], keep: "reset" }, true);
  },

  async saveChoice(selection, redraw) {
    const url = this.chooserTarget.dataset.url;
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const response = await fetch(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ selection, sender: this.sender }),
    }).catch(() => null);
    if (!response?.ok) return;

    const saved = await response.json();
    this.closeChooser();
    if (redraw) this.reloadHere();
    else this.applyHidden(saved.selection?.hidden || []);
  },
};
